import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import {
  Breadcrumbs as SpectrumBreadcrumbs,
  Breadcrumb,
  Text,
  Skeleton,
  Divider,
} from "@react-spectrum/s2";

import { style } from "@react-spectrum/s2/style" with { type: "macro" };

const BreadcrumbsContext = createContext(null);

let nextRegistrationId = 0;

/**
 * Holds the breadcrumb trail currently registered by the active view. Views
 * register a trail via {@link useBreadcrumbs} while mounted; the most recently
 * registered trail wins so the rendered breadcrumbs always reflect the view the
 * user is looking at.
 */
export const BreadcrumbsProvider = ({ children }) => {
  const [registrations, setRegistrations] = useState([]);

  const register = useCallback((id, crumbs) => {
    setRegistrations((prev) => [
      ...prev.filter((entry) => entry.id !== id),
      { id, crumbs },
    ]);
  }, []);

  const unregister = useCallback((id) => {
    setRegistrations((prev) => prev.filter((entry) => entry.id !== id));
  }, []);

  const crumbs = registrations[registrations.length - 1]?.crumbs ?? [];

  return (
    <BreadcrumbsContext.Provider value={{ crumbs, register, unregister }}>
      {children}
    </BreadcrumbsContext.Provider>
  );
};

BreadcrumbsProvider.propTypes = {
  children: PropTypes.node,
};

/**
 * Register a breadcrumb trail for the current view. The trail is shown while the
 * view is mounted and cleared on unmount, letting each route decide what to
 * display on render.
 *
 * @param {Array<{ label: string, href?: string, isLoading?: boolean }>} crumbs
 */
export const useBreadcrumbs = (crumbs) => {
  const context = useContext(BreadcrumbsContext);
  const register = context?.register;
  const unregister = context?.unregister;

  const idRef = useRef(null);
  if (idRef.current === null) {
    idRef.current = `breadcrumbs-${nextRegistrationId++}`;
  }

  // Keep the latest crumbs available to the effect without making its identity
  // the dependency (the array is recreated every render). A serialized key of
  // the primitive fields drives re-registration instead.
  const crumbsRef = useRef(crumbs);
  crumbsRef.current = crumbs;
  const key = JSON.stringify(crumbs ?? []);

  useEffect(() => {
    if (!register || !unregister) {
      return;
    }
    const id = idRef.current;
    register(id, crumbsRef.current ?? []);
    return () => unregister(id);
  }, [register, unregister, key]);
};

export const Breadcrumbs = () => {
  const { crumbs } = useContext(BreadcrumbsContext) ?? { crumbs: [] };

  if (!crumbs.length) {
    return null;
  }

  return (
    <>
      <Divider
        orientation="vertical"
        size="M"
        styles={style({ gridArea: "title", marginX: 8, marginY: 8 })}
      />
      <SpectrumBreadcrumbs
        size="M"
        styles={style({ gridArea: "title", flexBasis: "auto", flexGrow: 0 })}
      >
        {crumbs.map((crumb) => (
          <Breadcrumb key={crumb.href ?? crumb.label} href={crumb.href}>
            <Skeleton isLoading={!!crumb.isLoading}>
              <Text>{crumb.label}</Text>
            </Skeleton>
          </Breadcrumb>
        ))}
      </SpectrumBreadcrumbs>
    </>
  );
};
